import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  User,
  Lock,
  CreditCard,
  ArrowLeft,
  Save,
  Loader2,
  Check,
  Plus,
  Trash2,
  Shield,
  Calendar,
  MapPin,
  Building,
  Mail,
  Phone,
  AlertCircle,
  Star,
  RefreshCw,
  Share2,
  Copy,
  CheckCircle,
  Eye,
  Code,
  Upload,
  Image as ImageIcon,
  X,
  Palette,
  RotateCcw,
  Download,
  FileText,
  AlertTriangle,
  UserX,
  Clock,
  Globe
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import SubscriptionManager from '../components/billing/SubscriptionManager';

import axios from 'axios';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default logo URL
const DEFAULT_LOGO = '/mpm-logo.png';

// Common timezones list
const TIMEZONES = [
  { value: 'auto', label: 'Auto-detect (Phone Default)' },
  { value: 'America/New_York', label: 'Eastern Time (ET) - New York' },
  { value: 'America/Chicago', label: 'Central Time (CT) - Chicago' },
  { value: 'America/Denver', label: 'Mountain Time (MT) - Denver' },
  { value: 'America/Phoenix', label: 'Arizona Time - Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles' },
  { value: 'America/Anchorage', label: 'Alaska Time - Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time - Honolulu' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time - Puerto Rico' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' }
];

// Get browser's timezone
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'America/New_York';
  }
};

// Generate HTML signature for external email clients
const generateSignatureHTML = (referralCode, displayName, avatarUrl = null) => {
  const signupUrl = `${window.location.origin}/register?ref=${referralCode}`;
  const imageUrl = avatarUrl || DEFAULT_LOGO;
  const imageStyle = avatarUrl 
    ? 'border-radius: 50%; object-fit: cover;' 
    : 'border-radius: 8px;';
  
  return `<!-- MyPatriotMail Email Signature -->
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; max-width: 500px;">
  <tr>
    <td style="padding: 15px 0; border-top: 3px solid #6D4AFF;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td style="vertical-align: top; padding-right: 15px;">
            <!-- Profile Picture -->
            <img src="${imageUrl}" alt="${displayName}" width="60" height="60" style="${imageStyle}" />
          </td>
          <td style="vertical-align: top;">
            <p style="margin: 0 0 5px 0; font-size: 16px; font-weight: bold; color: #1a1a2e;">
              ${displayName}
            </p>
            <p style="margin: 0 0 10px 0; font-size: 13px; color: #6D4AFF; font-weight: 600;">
              🦅 Proud MyPatriotMail User
            </p>
            <p style="margin: 0; font-size: 12px; color: #666666; line-height: 1.5;">
              Join me on the most secure, American-owned email platform.<br/>
              <strong style="color: #1a1a2e;">Your privacy matters. Your data is never sold.</strong>
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding: 12px 0;">
      <table cellpadding="0" cellspacing="0" border="0" style="background: linear-gradient(135deg, #6D4AFF 0%, #5B3FD9 100%); border-radius: 8px;">
        <tr>
          <td style="padding: 12px 20px;">
            <a href="${signupUrl}" style="text-decoration: none; color: #ffffff; font-size: 14px; font-weight: bold; display: inline-block;">
              🛡️ Join MyPatriotMail FREE →
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding: 8px 0 0 0;">
      <p style="margin: 0; font-size: 11px; color: #999999;">
        Use my referral code: <strong style="color: #6D4AFF; font-family: monospace; background: #f0f0f0; padding: 2px 6px; border-radius: 4px;">${referralCode}</strong>
      </p>
    </td>
  </tr>
  <tr>
    <td style="padding: 10px 0 0 0; border-top: 1px solid #eeeeee; margin-top: 10px;">
      <p style="margin: 0; font-size: 10px; color: #bbbbbb;">
        🇺🇸 American-owned • 🔒 End-to-end encrypted • 🚫 We never sell your data
      </p>
    </td>
  </tr>
</table>
<!-- End MyPatriotMail Signature -->`;
};

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const { user, updateProfile, changePassword, refreshUser } = useAuth();
  const { isDarkMode, primaryColor, colorPreset, colorPresets, setColorFromPreset, setCustomColor, resetToDefault, saveUserPreferences, hasUnsavedChanges, loadUserPreferences } = useTheme();
  
  // Theme-aware colors
  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-gray-50';
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const bgTertiary = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  
  // Profile state
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Color preferences state
  const [savingColors, setSavingColors] = useState(false);
  const [colorsSaved, setColorsSaved] = useState(false);
  
  // Load user preferences on mount
  useEffect(() => {
    loadUserPreferences();
  }, [loadUserPreferences]);

  // Handle saving color preferences
  const handleSaveColorPreferences = async () => {
    setSavingColors(true);
    try {
      const success = await saveUserPreferences();
      if (success) {
        setColorsSaved(true);
        toast.success('Color preferences saved!');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingColors(false);
    }
  };
  
  // Password state
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [newCard, setNewCard] = useState({
    card_number: '',
    exp_month: '',
    exp_year: '',
    cvc: '',
    name: '',
    is_default: false
  });
  const [savingCard, setSavingCard] = useState(false);
  
  // Billing address state
  const [billingAddress, setBillingAddress] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [autoRenew, setAutoRenew] = useState(true);
  
  // Signature state
  const [signatureCopied, setSignatureCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [signatureAvatar, setSignatureAvatar] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = React.useRef(null);
  
  // MyPatriotMail Email Signature state
  const [emailSignature, setEmailSignature] = useState('');
  const [savingSignature, setSavingSignature] = useState(false);
  const [signatureEnabled, setSignatureEnabled] = useState(true);

  // Timezone state
  const [timezone, setTimezone] = useState('auto');
  const [savingTimezone, setSavingTimezone] = useState(false);
  const detectedTimezone = getBrowserTimezone();

  // Delete Account state
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = warning, 2 = confirmation

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '');
      setEmailSignature(user.email_signature || '');
      setSignatureEnabled(user.signature_enabled !== false);
      setTimezone(user.timezone || 'auto');
      fetchPaymentMethods();
      fetchBillingInfo();
      fetchSubscription();
    }
  }, [user]);

  const fetchPaymentMethods = async () => {
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API}/billing/payment-methods`);
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const fetchBillingInfo = async () => {
    try {
      const response = await axios.get(`${API}/billing/address`);
      if (response.data) {
        setBillingAddress(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch billing address:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription`);
      setSubscription(response.data);
      setAutoRenew(response.data?.auto_renew !== false);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }
    
    setSavingProfile(true);
    const result = await updateProfile(displayName);
    setSavingProfile(false);
    
    if (result.success) {
      toast.success('Profile updated successfully');
    } else {
      toast.error(result.error || 'Failed to update profile');
    }
  };

  const handleSaveTimezone = async () => {
    setSavingTimezone(true);
    try {
      await axios.put(`${API}/auth/timezone`, { timezone });
      toast.success('Timezone saved successfully');
    } catch (error) {
      console.error('Failed to save timezone:', error);
      toast.error('Failed to save timezone');
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (passwords.new !== passwords.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    
    setSavingPassword(true);
    const result = await changePassword(passwords.current, passwords.new);
    setSavingPassword(false);
    
    if (result.success) {
      toast.success('Password changed successfully');
      setPasswords({ current: '', new: '', confirm: '' });
    } else {
      toast.error(result.error || 'Failed to change password');
    }
  };

  // Save email signature
  const handleSaveEmailSignature = async () => {
    setSavingSignature(true);
    try {
      await axios.put(`${API}/auth/user/signature`, {
        signature: emailSignature,
        enabled: signatureEnabled
      });
      toast.success('Email signature saved!');
      await refreshUser();
    } catch (err) {
      toast.error('Failed to save signature');
    } finally {
      setSavingSignature(false);
    }
  };

  const handleAddCard = async () => {
    if (!newCard.card_number || !newCard.exp_month || !newCard.exp_year || !newCard.cvc) {
      toast.error('Please fill in all card details');
      return;
    }
    
    setSavingCard(true);
    try {
      await axios.post(`${API}/billing/payment-methods`, newCard);
      toast.success('Card added successfully');
      setAddCardOpen(false);
      setNewCard({
        card_number: '',
        exp_month: '',
        exp_year: '',
        cvc: '',
        name: '',
        is_default: false
      });
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add card');
    } finally {
      setSavingCard(false);
    }
  };

  const handleSetDefaultCard = async (cardId) => {
    try {
      await axios.put(`${API}/billing/payment-methods/${cardId}/default`);
      toast.success('Default card updated');
      fetchPaymentMethods();
    } catch (error) {
      toast.error('Failed to update default card');
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await axios.delete(`${API}/billing/payment-methods/${cardId}`);
      toast.success('Card removed');
      fetchPaymentMethods();
    } catch (error) {
      toast.error('Failed to remove card');
    }
  };

  const handleSaveBillingAddress = async () => {
    setSavingAddress(true);
    try {
      await axios.put(`${API}/billing/address`, billingAddress);
      toast.success('Billing address saved');
    } catch (error) {
      toast.error('Failed to save billing address');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleToggleAutoRenew = async (enabled) => {
    try {
      await axios.put(`${API}/billing/subscription/auto-renew`, { auto_renew: enabled });
      setAutoRenew(enabled);
      toast.success(enabled ? 'Auto-renewal enabled' : 'Auto-renewal disabled');
    } catch (error) {
      toast.error('Failed to update auto-renewal');
    }
  };

  const getCardIcon = (brand) => {
    return <CreditCard className="w-8 h-8 text-gray-400" />;
  };

  return (
    <div className={`min-h-screen ${bgPrimary}`}>
      {/* Header */}
      <div className={`${bgSecondary} border-b ${borderColor} px-6 py-4`}>
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/inbox')}
            className={`${textMuted} hover:${textPrimary}`}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className={`text-xl font-bold ${textPrimary}`}>Account Settings</h1>
            <p className={`text-sm ${textMuted}`}>{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6">
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className={`${bgSecondary} border ${borderColor} flex-wrap h-auto`}>
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Lock className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="customize" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Palette className="w-4 h-4 mr-2" />
              Customize
            </TabsTrigger>
            <TabsTrigger value="invite" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Share2 className="w-4 h-4 mr-2" />
              Signature
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardHeader>
                <CardTitle className={textPrimary}>Profile Information</CardTitle>
                <CardDescription className={textMuted}>
                  Update your account profile details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className={textSecondary}>Email Address</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="bg-[#252540] border-[#2d2d44] text-gray-400"
                  />
                  <p className="text-xs text-gray-500">Email cannot be changed</p>
                </div>

                <div className="space-y-2">
                  <Label className={textSecondary}>Display Name</Label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                    placeholder="Your display name"
                  />
                </div>

                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                >
                  {savingProfile ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Timezone Settings Card */}
            <Card className={`${bgSecondary} ${borderColor} mt-6`}>
              <CardHeader>
                <CardTitle className={`${textPrimary} flex items-center gap-2`}>
                  <Globe className="w-5 h-5 text-[#6D4AFF]" />
                  Timezone Settings
                </CardTitle>
                <CardDescription className={textMuted}>
                  Set your preferred timezone for displaying dates and times
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className={textSecondary}>Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger className={`${bgTertiary} ${borderColor} ${textPrimary}`}>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent className={`${bgSecondary} ${borderColor}`}>
                      {TIMEZONES.map((tz) => (
                        <SelectItem 
                          key={tz.value} 
                          value={tz.value}
                          className={`${textSecondary} focus:bg-[#6D4AFF]/20`}
                        >
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {timezone === 'auto' && (
                    <p className={`text-xs ${textMuted} flex items-center gap-1`}>
                      <Clock className="w-3 h-3" />
                      Detected: {detectedTimezone}
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleSaveTimezone}
                  disabled={savingTimezone}
                  className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                >
                  {savingTimezone ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Timezone
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Export Data Card */}
            <Card className="bg-[#1a1a2e] border-[#2d2d44] mt-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#6D4AFF]" />
                  Export Your Data
                </CardTitle>
                <CardDescription className={textMuted}>
                  Download a copy of your personal information and emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-400">
                  You can export all your data at any time. This includes your profile information, 
                  all emails (sent and received), and your contacts. The export will be downloaded 
                  as a CSV file that you can open in any spreadsheet application.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${API}/export/my-data`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        
                        if (!response.ok) throw new Error('Export failed');
                        
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `mypatriotmail_export_${new Date().toISOString().split('T')[0]}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                        
                        toast.success('Data exported successfully!');
                      } catch (err) {
                        toast.error('Failed to export data. Please try again.');
                      }
                    }}
                    variant="outline"
                    className="border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF] hover:text-white"
                    data-testid="export-csv-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export as CSV
                  </Button>
                  
                  <Button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${API}/export/my-data/json`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        
                        if (!response.ok) throw new Error('Export failed');
                        
                        const data = await response.json();
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `mypatriotmail_export_${new Date().toISOString().split('T')[0]}.json`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                        
                        toast.success('Data exported successfully!');
                      } catch (err) {
                        toast.error('Failed to export data. Please try again.');
                      }
                    }}
                    variant="outline"
                    className="border-[#2d2d44] text-gray-400 hover:bg-[#252540] hover:text-white"
                    data-testid="export-json-btn"
                  >
                    <Code className="w-4 h-4 mr-2" />
                    Export as JSON
                  </Button>
                </div>
                
                <p className="text-xs text-gray-500">
                  Your data export includes: Profile info, all emails (content, metadata), and contacts.
                  Attachments are not included in the export file.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardHeader>
                <CardTitle className={textPrimary}>Change Password</CardTitle>
                <CardDescription className={textMuted}>
                  Update your account password
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className={textSecondary}>Current Password</Label>
                  <Input
                    type="password"
                    value={passwords.current}
                    onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                    className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={textSecondary}>New Password</Label>
                  <Input
                    type="password"
                    value={passwords.new}
                    onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                    className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                    placeholder="Min 8 characters"
                  />
                </div>

                <div className="space-y-2">
                  <Label className={textSecondary}>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                    className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                  />
                </div>

                <Button
                  onClick={handleChangePassword}
                  disabled={savingPassword}
                  className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                >
                  {savingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Danger Zone - Delete Account (Hidden for protected accounts) */}
            {user?.email?.toLowerCase() !== 'mel@mypatriotmail.org' && user?.role !== 'admin' && (
            <Card className="bg-[#1a1a2e] border-red-500/30 mt-6">
              <CardHeader>
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Danger Zone
                </CardTitle>
                <CardDescription className={textMuted}>
                  Permanently delete your account and all associated data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-gray-300">
                    Deleting your account will <span className="text-red-400 font-semibold">permanently remove</span> all your data including:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-gray-400">
                    <li>• All emails (sent and received)</li>
                    <li>• Your contacts and labels</li>
                    <li>• Your profile and subscription</li>
                    <li>• All messages and conversations</li>
                  </ul>
                  <p className="mt-3 text-sm text-red-400 font-medium">
                    ⚠️ This action cannot be undone. There is no way to recover your account or data.
                  </p>
                </div>
                
                <Button
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  onClick={() => setDeleteAccountModalOpen(true)}
                  data-testid="open-delete-account-btn"
                >
                  <UserX className="w-4 h-4 mr-2" />
                  Delete My Account
                </Button>
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            {/* Subscription Management (plans, upgrade/downgrade, history) */}
            <SubscriptionManager isDarkMode={isDarkMode} />

            <Separator className={borderColor} />

            {/* Payment Methods */}
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={textPrimary}>Payment Methods</CardTitle>
                    <CardDescription className={textMuted}>
                      Manage your credit cards for auto-billing
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setAddCardOpen(true)}
                    className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Card
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingPayments ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
                  </div>
                ) : paymentMethods.length === 0 ? (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No payment methods on file</p>
                    <p className="text-sm text-gray-500">Add a card for auto-recurring billing</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentMethods.map((card) => (
                      <div
                        key={card.id}
                        className={`flex items-center justify-between p-4 bg-[#252540] rounded-lg border ${
                          card.is_default ? 'border-[#6D4AFF]' : 'border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          {getCardIcon(card.brand)}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">
                                •••• •••• •••• {card.last4}
                              </span>
                              {card.is_default && (
                                <Badge className="bg-[#6D4AFF]/20 text-[#6D4AFF] border-[#6D4AFF]/30">
                                  Default
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-400">
                              Expires {card.exp_month}/{card.exp_year}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!card.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetDefaultCard(card.id)}
                              className="text-gray-400 hover:text-white"
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCard(card.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Billing Address */}
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardHeader>
                <CardTitle className={textPrimary}>Billing Address</CardTitle>
                <CardDescription className={textMuted}>
                  Address associated with your payment methods
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={textSecondary}>Full Name</Label>
                    <Input
                      value={billingAddress.name}
                      onChange={(e) => setBillingAddress({ ...billingAddress, name: e.target.value })}
                      className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={textSecondary}>Phone</Label>
                    <Input
                      value={billingAddress.phone}
                      onChange={(e) => setBillingAddress({ ...billingAddress, phone: e.target.value })}
                      className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={textSecondary}>Address Line 1</Label>
                  <Input
                    value={billingAddress.address_line1}
                    onChange={(e) => setBillingAddress({ ...billingAddress, address_line1: e.target.value })}
                    className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="space-y-2">
                  <Label className={textSecondary}>Address Line 2</Label>
                  <Input
                    value={billingAddress.address_line2}
                    onChange={(e) => setBillingAddress({ ...billingAddress, address_line2: e.target.value })}
                    className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                    placeholder="Apt, Suite, etc. (optional)"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className={textSecondary}>City</Label>
                    <Input
                      value={billingAddress.city}
                      onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                      className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={textSecondary}>State</Label>
                    <Input
                      value={billingAddress.state}
                      onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
                      className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={textSecondary}>ZIP Code</Label>
                    <Input
                      value={billingAddress.postal_code}
                      onChange={(e) => setBillingAddress({ ...billingAddress, postal_code: e.target.value })}
                      className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={textSecondary}>Country</Label>
                    <Select
                      value={billingAddress.country}
                      onValueChange={(v) => setBillingAddress({ ...billingAddress, country: v })}
                    >
                      <SelectTrigger className={`${bgTertiary} ${borderColor} ${textPrimary}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                        <SelectItem value="US" className="text-white">United States</SelectItem>
                        <SelectItem value="CA" className="text-white">Canada</SelectItem>
                        <SelectItem value="GB" className="text-white">United Kingdom</SelectItem>
                        <SelectItem value="AU" className="text-white">Australia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  onClick={handleSaveBillingAddress}
                  disabled={savingAddress}
                  className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                >
                  {savingAddress ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Address
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customize Tab */}
          <TabsContent value="customize">
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Palette className="w-5 h-5" style={{ color: primaryColor }} />
                  Customize Your Dashboard
                </CardTitle>
                <CardDescription className={textMuted}>
                  Personalize your MyPatriotMail experience with custom button colors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Color Preview */}
                <div className="p-4 bg-[#252540] rounded-xl border border-[#2d2d44]">
                  <Label className="text-gray-300 mb-3 block">Current Button Color</Label>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-16 h-16 rounded-xl shadow-lg"
                      style={{ backgroundColor: primaryColor }}
                    />
                    <div>
                      <p className="text-white font-medium">
                        {colorPreset === 'custom' ? 'Custom Color' : colorPresets[colorPreset]?.name || 'Custom'}
                      </p>
                      <p className="text-sm text-gray-400 font-mono">{primaryColor}</p>
                    </div>
                  </div>
                </div>

                {/* Color Presets */}
                <div className="space-y-3">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Color Presets
                  </Label>
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(colorPresets).map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => setColorFromPreset(key)}
                        className={`group relative flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                          colorPreset === key 
                            ? 'bg-[#252540] ring-2 ring-offset-2 ring-offset-[#1a1a2e]'
                            : 'hover:bg-[#252540]'
                        }`}
                        style={{ 
                          ringColor: colorPreset === key ? preset.primary : 'transparent'
                        }}
                        data-testid={`color-preset-${key}`}
                      >
                        <div 
                          className={`w-10 h-10 rounded-full shadow-lg transition-transform group-hover:scale-110 ${
                            colorPreset === key ? 'ring-2 ring-white/50' : ''
                          }`}
                          style={{ backgroundColor: preset.primary }}
                        />
                        <span className="text-xs text-gray-400 text-center leading-tight">
                          {preset.name.replace(' (Default)', '')}
                        </span>
                        {colorPreset === key && (
                          <Check className="absolute top-2 right-2 w-4 h-4 text-green-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Color Picker */}
                <div className="space-y-3">
                  <Label className={textSecondary}>Custom Color</Label>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setCustomColor(e.target.value)}
                        className="w-16 h-12 rounded-lg cursor-pointer border-2 border-[#2d2d44] bg-transparent"
                        data-testid="custom-color-picker"
                      />
                    </div>
                    <Input
                      value={primaryColor}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                          if (val.length === 7) {
                            setCustomColor(val);
                          }
                        }
                      }}
                      className="bg-[#252540] border-[#2d2d44] text-white font-mono w-32"
                      placeholder="#6D4AFF"
                      maxLength={7}
                      data-testid="custom-color-input"
                    />
                    <Button
                      variant="outline"
                      onClick={resetToDefault}
                      className="border-[#2d2d44] text-gray-400 hover:text-white hover:bg-[#252540]"
                      data-testid="reset-color-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                </div>

                {/* Preview Section */}
                <div className="space-y-3">
                  <Label className={textSecondary}>Live Preview</Label>
                  <div className="p-4 bg-[#252540] rounded-xl border border-[#2d2d44] space-y-4">
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        style={{ backgroundColor: primaryColor }}
                        className="text-white hover:opacity-90"
                        data-testid="preview-primary-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Primary Button
                      </Button>
                      <Button 
                        variant="outline"
                        style={{ borderColor: primaryColor, color: primaryColor }}
                        className="hover:bg-opacity-10"
                        data-testid="preview-outline-btn"
                      >
                        Outline Button
                      </Button>
                      <Button 
                        variant="ghost"
                        style={{ color: primaryColor }}
                        className="hover:bg-opacity-10"
                        data-testid="preview-ghost-btn"
                      >
                        Ghost Button
                      </Button>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge style={{ backgroundColor: `${primaryColor}20`, color: primaryColor, borderColor: `${primaryColor}50` }} className="border">
                        Badge Example
                      </Badge>
                      <div className="flex items-center gap-2">
                        <Switch checked={true} style={{ backgroundColor: primaryColor }} />
                        <span className="text-sm text-gray-400">Toggle Example</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: primaryColor }} />
                      <span className="text-sm text-gray-300">Icons and accents will use this color</span>
                    </div>
                  </div>
                </div>

                {/* Info Note */}
                <div className="p-4 bg-gradient-to-r from-[#6D4AFF]/10 to-purple-500/5 rounded-xl border border-[#6D4AFF]/30">
                  <p className="text-sm text-gray-300">
                    <strong className="text-white">Note:</strong> Your color preference is saved to your account and will apply across all buttons, icons, and accent elements throughout the app. Changes are previewed immediately but require saving to persist.
                  </p>
                </div>

                {/* Save Button */}
                <div className="flex items-center justify-between pt-4 border-t border-[#2d2d44]">
                  <div className="flex items-center gap-2">
                    {hasUnsavedChanges && (
                      <span className="text-sm text-yellow-400 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Unsaved changes
                      </span>
                    )}
                    {!hasUnsavedChanges && colorsSaved && (
                      <span className="text-sm text-green-400 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        All changes saved
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={handleSaveColorPreferences}
                    disabled={savingColors || !hasUnsavedChanges}
                    style={{ backgroundColor: hasUnsavedChanges ? primaryColor : undefined }}
                    className={`${hasUnsavedChanges ? 'text-white hover:opacity-90' : 'bg-[#252540] text-gray-400'}`}
                    data-testid="save-colors-btn"
                  >
                    {savingColors ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Color Preferences
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Signature Tab */}
          <TabsContent value="invite">
            <Card className={`${bgSecondary} ${borderColor}`}>
              <CardContent className="space-y-6 pt-6">
                {/* MyPatriotMail Email Signature - MOVED TO TOP */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-semibold flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#6D4AFF]" />
                        MyPatriotMail Email Signature
                      </h4>
                      <p className="text-sm text-gray-400 mt-1">
                        This signature will be automatically added to emails you send from MyPatriotMail
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-gray-400">Enable</Label>
                      <Switch
                        checked={signatureEnabled}
                        onCheckedChange={setSignatureEnabled}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className={textSecondary}>Your Signature</Label>
                    <Textarea
                      value={emailSignature}
                      onChange={(e) => setEmailSignature(e.target.value)}
                      placeholder="Enter your email signature...

Example:
Best regards,
John Doe
Patriot Member since 2024
🦅 Sent securely via MyPatriotMail"
                      className="bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-600 min-h-[150px]"
                    />
                  </div>

                  {/* Signature Preview */}
                  {emailSignature && (
                    <div className="p-4 bg-[#252540] rounded-xl border border-[#2d2d44]">
                      <Label className="text-gray-400 text-xs mb-2 block">Preview</Label>
                      <div className="text-sm text-gray-300 whitespace-pre-wrap border-t border-[#2d2d44] pt-3 mt-2">
                        {emailSignature}
                      </div>
                    </div>
                  )}

                  {/* Quick Templates */}
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-xs">Quick Templates</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEmailSignature(`Best regards,\n${user?.display_name || 'Your Name'}\n🦅 Sent securely via MyPatriotMail`)}
                        className="border-[#2d2d44] text-gray-400 hover:text-white text-xs"
                      >
                        Simple
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEmailSignature(`Best regards,\n${user?.display_name || 'Your Name'}\n${user?.email || 'email@mypatriotmail.org'}\n\n🦅 Secured by MyPatriotMail\n🔒 End-to-end encrypted`)}
                        className="border-[#2d2d44] text-gray-400 hover:text-white text-xs"
                      >
                        Professional
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEmailSignature(`${user?.display_name || 'Your Name'}\nProud Patriot 🇺🇸\n\n━━━━━━━━━━━━━━━━━━\n🦅 MyPatriotMail.org\nSecure • Private • American\n\nJoin me: ${window.location.origin}/register?ref=${user?.referral_code || ''}`)}
                        className="border-[#2d2d44] text-gray-400 hover:text-white text-xs"
                      >
                        With Referral Link
                      </Button>
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSaveEmailSignature}
                    disabled={savingSignature}
                    className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                  >
                    {savingSignature ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Email Signature
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Card Dialog */}
      <Dialog open={addCardOpen} onOpenChange={setAddCardOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Payment Method</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add a credit or debit card for auto-billing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={textSecondary}>Name on Card</Label>
              <Input
                value={newCard.name}
                onChange={(e) => setNewCard({ ...newCard, name: e.target.value })}
                className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                placeholder="John Doe"
              />
            </div>

            <div className="space-y-2">
              <Label className={textSecondary}>Card Number</Label>
              <Input
                value={newCard.card_number}
                onChange={(e) => setNewCard({ ...newCard, card_number: e.target.value.replace(/\D/g, '').slice(0, 16) })}
                className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                placeholder="1234 5678 9012 3456"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className={textSecondary}>Month</Label>
                <Select
                  value={newCard.exp_month}
                  onValueChange={(v) => setNewCard({ ...newCard, exp_month: v })}
                >
                  <SelectTrigger className={`${bgTertiary} ${borderColor} ${textPrimary}`}>
                    <SelectValue placeholder="MM" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                    {Array.from({ length: 12 }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1).padStart(2, '0')} className="text-white">
                        {String(i + 1).padStart(2, '0')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>Year</Label>
                <Select
                  value={newCard.exp_year}
                  onValueChange={(v) => setNewCard({ ...newCard, exp_year: v })}
                >
                  <SelectTrigger className={`${bgTertiary} ${borderColor} ${textPrimary}`}>
                    <SelectValue placeholder="YY" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                    {Array.from({ length: 10 }, (_, i) => (
                      <SelectItem key={i} value={String(new Date().getFullYear() + i).slice(-2)} className="text-white">
                        {new Date().getFullYear() + i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>CVC</Label>
                <Input
                  value={newCard.cvc}
                  onChange={(e) => setNewCard({ ...newCard, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                  placeholder="123"
                  maxLength={4}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-[#252540] rounded-lg">
              <Switch
                checked={newCard.is_default}
                onCheckedChange={(checked) => setNewCard({ ...newCard, is_default: checked })}
              />
              <Label className={textSecondary}>Set as default payment method</Label>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Shield className="w-4 h-4" />
              <span>Your card information is encrypted and secure</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddCardOpen(false)} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleAddCard}
              disabled={savingCard}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
            >
              {savingCard ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Card
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Modal */}
      <Dialog 
        open={deleteAccountModalOpen} 
        onOpenChange={(open) => {
          setDeleteAccountModalOpen(open);
          if (!open) {
            setDeleteStep(1);
            setDeleteConfirmation('');
            setDeletePassword('');
          }
        }}
      >
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-lg">
          {deleteStep === 1 ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Delete Your Account?
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  Please read this carefully before proceeding
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <h4 className="text-white font-medium mb-2">⚠️ This action is permanent</h4>
                  <p className="text-sm text-gray-300">
                    Once you delete your account, there is <span className="text-red-400 font-semibold">no way to recover it</span>. 
                    All your data will be permanently erased from our servers.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-white font-medium">What will be deleted:</h4>
                  <ul className="space-y-1 text-sm text-gray-400">
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-400" />
                      All your emails (inbox, sent, drafts, etc.)
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-400" />
                      Your contacts and address book
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-400" />
                      Your profile and account settings
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-400" />
                      Your subscription and billing history
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-400" />
                      All messages and conversations
                    </li>
                  </ul>
                </div>
                
                <div className="p-4 bg-[#6D4AFF]/10 border border-[#6D4AFF]/30 rounded-lg">
                  <h4 className="text-[#6D4AFF] font-medium mb-2 flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Want to save your data first?
                  </h4>
                  <p className="text-sm text-gray-300 mb-3">
                    You can download a copy of all your data before deleting your account.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF] hover:text-white"
                    onClick={() => {
                      setDeleteAccountModalOpen(false);
                      setActiveTab('profile');
                      // Scroll to export section
                      setTimeout(() => {
                        document.querySelector('[data-testid="export-csv-btn"]')?.scrollIntoView({ behavior: 'smooth' });
                      }, 300);
                    }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Go to Export Data
                  </Button>
                </div>
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteAccountModalOpen(false)}
                  className="border-[#2d2d44] text-gray-400 hover:bg-[#252540]"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteStep(2)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="continue-delete-btn"
                >
                  I Understand, Continue
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-red-400 flex items-center gap-2">
                  <UserX className="w-5 h-5" />
                  Confirm Account Deletion
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  Enter your password and type the confirmation phrase
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className={textSecondary}>Your Password</Label>
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Enter your current password"
                    className={`${bgTertiary} ${borderColor} ${textPrimary}`}
                    data-testid="delete-password-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className={textSecondary}>
                    Type <span className="text-red-400 font-mono">DELETE MY ACCOUNT</span> to confirm
                  </Label>
                  <Input
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className="bg-[#252540] border-[#2d2d44] text-white font-mono"
                    data-testid="delete-confirmation-input"
                  />
                </div>
                
                {deleteConfirmation && deleteConfirmation !== 'DELETE MY ACCOUNT' && (
                  <p className="text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Please type exactly: DELETE MY ACCOUNT
                  </p>
                )}
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteStep(1)}
                  className="border-[#2d2d44] text-gray-400 hover:bg-[#252540]"
                >
                  Go Back
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleteConfirmation !== 'DELETE MY ACCOUNT' || !deletePassword || deletingAccount}
                  onClick={async () => {
                    setDeletingAccount(true);
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API}/auth/account`, {
                        method: 'DELETE',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          password: deletePassword,
                          confirmation: deleteConfirmation
                        })
                      });
                      
                      const data = await response.json();
                      
                      if (!response.ok) {
                        throw new Error(data.detail || 'Failed to delete account');
                      }
                      
                      toast.success('Your account has been deleted. Goodbye!');
                      localStorage.removeItem('token');
                      window.location.href = '/';
                    } catch (err) {
                      toast.error(err.message || 'Failed to delete account');
                    } finally {
                      setDeletingAccount(false);
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-btn"
                >
                  {deletingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Permanently Delete Account
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
